import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { BarChart3, Loader2, Sparkles, Zap } from "lucide-react";
import {
  AUDIENCES,
  CHANNELS,
  HOLDOUT_HELP,
  SEND_TIME_HELP,
  audienceLabel,
  channelLabel,
  errorMessage,
  fetchAutomationConversions,
  fetchConversionGoals,
  formatNumber,
  formatRelative,
  triggerLabel,
  type Audience,
  type Automation,
  type AutomationTrigger,
  type AutomationsResponse,
  type Channel,
  type ConversionGoal,
  type ConversionReport,
  type Template,
  type TemplatesResponse,
} from "@/lib/comms";
import { EmptyState, ErrorState } from "./shared";
import ConversionPanel from "./ConversionPanel";

/** Loaded only when someone asks: attribution is a real query, not something to run on every render. */
function ConversionsSection({ automationId, goals }: { automationId: string; goals: ConversionGoal[] }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error, refetch, isFetching } = useQuery<ConversionReport>({
    queryKey: ["/api/admin/comms/automations", automationId, "conversions"],
    queryFn: () => fetchAutomationConversions(automationId),
    enabled: open,
  });

  return (
    <div className="border-t pt-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Conversions</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => (open ? refetch() : setOpen(true))}
          disabled={isFetching}
          data-testid={`button-measure-${automationId}`}
        >
          {isFetching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
          {open ? "Measure again" : "Measure"}
        </Button>
      </div>
      {!open ? (
        <p className="text-xs text-muted-foreground">
          Counts how many of the people this automation reached went on to do the thing it is meant to cause, against the holdout it kept back.
        </p>
      ) : error ? (
        <p className="text-sm text-destructive">{errorMessage(error)}</p>
      ) : isLoading || !data ? (
        <Skeleton className="h-24" />
      ) : (
        <ConversionPanel report={data} goals={goals} title="Measured" />
      )}
    </div>
  );
}

function AutomationCard({
  automation,
  trigger,
  templates,
  goals,
  triggers,
  onPatch,
  pending,
}: {
  automation: Automation;
  trigger: AutomationTrigger | undefined;
  templates: Template[];
  goals: ConversionGoal[];
  triggers: AutomationTrigger[];
  onPatch: (id: string, body: Record<string, unknown>) => void;
  pending: boolean;
}) {
  const [capDays, setCapDays] = useState(automation.capPerContactDays === null || automation.capPerContactDays === undefined ? "" : String(automation.capPerContactDays));
  const [repeatDays, setRepeatDays] = useState(automation.repeatEveryDays === null || automation.repeatEveryDays === undefined ? "" : String(automation.repeatEveryDays));
  const [holdout, setHoldout] = useState(String(automation.holdoutPercent ?? 0));
  const [windowDays, setWindowDays] = useState(String(automation.conversionWindowDays ?? 7));

  useEffect(() => {
    setCapDays(automation.capPerContactDays === null || automation.capPerContactDays === undefined ? "" : String(automation.capPerContactDays));
  }, [automation.capPerContactDays]);

  useEffect(() => {
    setRepeatDays(automation.repeatEveryDays === null || automation.repeatEveryDays === undefined ? "" : String(automation.repeatEveryDays));
  }, [automation.repeatEveryDays]);

  useEffect(() => { setHoldout(String(automation.holdoutPercent ?? 0)); }, [automation.holdoutPercent]);
  useEffect(() => { setWindowDays(String(automation.conversionWindowDays ?? 7)); }, [automation.conversionWindowDays]);

  const commitNumber = (raw: string, key: "capPerContactDays" | "repeatEveryDays", current: number | null) => {
    const trimmed = raw.trim();
    const next = trimmed === "" ? null : Number(trimmed);
    if (next !== null && (Number.isNaN(next) || next < 0)) return;
    if (next === current) return;
    onPatch(automation.id, { [key]: next });
  };

  const commitBounded = (raw: string, key: "holdoutPercent" | "conversionWindowDays", min: number, max: number, current: number) => {
    const n = Number(raw);
    const next = raw.trim() === "" || Number.isNaN(n) ? current : Math.min(max, Math.max(min, Math.round(n)));
    if (next === current) {
      // Snap the box back to the value that is actually stored.
      if (key === "holdoutPercent") setHoldout(String(current)); else setWindowDays(String(current));
      return;
    }
    onPatch(automation.id, { [key]: next });
  };

  const channelTemplates = templates.filter(t => t.channel === automation.channel);
  const goalId = automation.conversionGoal || "none";
  const selectedGoal = goals.find(g => g.id === goalId) || null;

  return (
    <Card data-testid={`card-automation-${automation.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">{automation.name}</CardTitle>
            <CardDescription>
              {trigger?.description || `Fires on: ${triggerLabel(automation.trigger, triggers)}`}
            </CardDescription>
            <div className="flex flex-wrap gap-1.5 pt-2">
              <Badge variant="outline">{triggerLabel(automation.trigger, triggers)}</Badge>
              <Badge variant="outline">{channelLabel(automation.channel)}</Badge>
              <Badge variant="outline">{audienceLabel(automation.audience)}</Badge>
              <Badge className={`border-0 ${automation.category === "marketing" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"}`}>
                {automation.category}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label htmlFor={`enabled-${automation.id}`} className="text-sm cursor-pointer">
              {automation.enabled ? "On" : "Off"}
            </Label>
            <Switch
              id={`enabled-${automation.id}`}
              checked={automation.enabled}
              disabled={pending}
              onCheckedChange={v => onPatch(automation.id, { enabled: v })}
              data-testid={`switch-automation-${automation.id}`}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor={`template-${automation.id}`}>Template</Label>
            <Select
              value={automation.templateId || ""}
              onValueChange={v => onPatch(automation.id, { templateId: v })}
            >
              <SelectTrigger id={`template-${automation.id}`}>
                <SelectValue placeholder={channelTemplates.length === 0 ? `No ${channelLabel(automation.channel).toLowerCase()} template` : "Choose a template"} />
              </SelectTrigger>
              <SelectContent>
                {channelTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`channel-${automation.id}`}>Channel</Label>
            <Select value={automation.channel} onValueChange={v => onPatch(automation.id, { channel: v as Channel })}>
              <SelectTrigger id={`channel-${automation.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHANNELS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`cap-${automation.id}`}>Cap, days between messages</Label>
            <Input
              id={`cap-${automation.id}`}
              type="number"
              min={0}
              value={capDays}
              onChange={e => setCapDays(e.target.value)}
              onBlur={() => commitNumber(capDays, "capPerContactDays", automation.capPerContactDays)}
              onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              placeholder="Use the default"
              data-testid={`input-cap-${automation.id}`}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`repeat-${automation.id}`}>Repeat every, days</Label>
            <Input
              id={`repeat-${automation.id}`}
              type="number"
              min={0}
              value={repeatDays}
              onChange={e => setRepeatDays(e.target.value)}
              onBlur={() => commitNumber(repeatDays, "repeatEveryDays", automation.repeatEveryDays)}
              onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              placeholder="Once per contact"
              data-testid={`input-repeat-${automation.id}`}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`audience-${automation.id}`}>Audience</Label>
          <Select value={automation.audience} onValueChange={v => onPatch(automation.id, { audience: v as Audience })}>
            <SelectTrigger id={`audience-${automation.id}`} className="sm:w-[260px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AUDIENCES.map(a => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Experiments and measurement */}
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 border-t pt-3">
          <div className="space-y-1.5">
            <Label htmlFor={`holdout-${automation.id}`}>Holdout, percent</Label>
            <Input
              id={`holdout-${automation.id}`}
              type="number"
              min={0}
              max={50}
              value={holdout}
              onChange={e => setHoldout(e.target.value)}
              onBlur={() => commitBounded(holdout, "holdoutPercent", 0, 50, automation.holdoutPercent ?? 0)}
              onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              data-testid={`input-holdout-${automation.id}`}
            />
            <p className="text-xs text-muted-foreground leading-snug">{HOLDOUT_HELP}</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`goal-${automation.id}`}>Conversion goal</Label>
            <Select value={goalId} onValueChange={v => onPatch(automation.id, { conversionGoal: v })}>
              <SelectTrigger id={`goal-${automation.id}`} data-testid={`select-goal-${automation.id}`}>
                <SelectValue placeholder={goals.length === 0 ? "Loading goals" : "Choose a goal"} />
              </SelectTrigger>
              <SelectContent>
                {goals.map(g => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedGoal?.description ? <p className="text-xs text-muted-foreground leading-snug">{selectedGoal.description}</p> : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`window-${automation.id}`}>Conversion window, days</Label>
            <Input
              id={`window-${automation.id}`}
              type="number"
              min={1}
              max={30}
              value={windowDays}
              onChange={e => setWindowDays(e.target.value)}
              onBlur={() => commitBounded(windowDays, "conversionWindowDays", 1, 30, automation.conversionWindowDays ?? 7)}
              onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              data-testid={`input-window-${automation.id}`}
            />
            <p className="text-xs text-muted-foreground leading-snug">How long after a send the action still counts.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`sto-${automation.id}`}>Send at the best time</Label>
            <div className="h-10 flex items-center">
              <Switch
                id={`sto-${automation.id}`}
                checked={!!automation.sendTimeOptimized}
                disabled={pending}
                onCheckedChange={v => onPatch(automation.id, { sendTimeOptimized: v })}
                data-testid={`switch-sto-${automation.id}`}
              />
            </div>
            <p className="text-xs text-muted-foreground leading-snug">{SEND_TIME_HELP}</p>
          </div>
        </div>

        <ConversionsSection automationId={automation.id} goals={goals} />

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground border-t pt-3">
          <span>Queued: <span className="font-semibold text-foreground">{formatNumber(automation.stats?.queued)}</span></span>
          <span>Sent: <span className="font-semibold text-foreground">{formatNumber(automation.stats?.sent)}</span></span>
          <span>Opened: <span className="font-semibold text-foreground">{formatNumber(automation.stats?.opened)}</span></span>
          <span>Clicked: <span className="font-semibold text-foreground">{formatNumber(automation.stats?.clicked)}</span></span>
          <span>Failed: <span className="font-semibold text-foreground">{formatNumber(automation.stats?.failed)}</span></span>
          <span>Last run {formatRelative(automation.stats?.lastRunAt)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AutomationsTab() {
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<AutomationsResponse>({ queryKey: ["/api/admin/comms/automations"] });
  const { data: templatesData } = useQuery<TemplatesResponse>({ queryKey: ["/api/admin/comms/templates"] });
  const { data: goalsData } = useQuery<{ goals: ConversionGoal[] }>({
    queryKey: ["/api/admin/comms/conversions/goals"],
    queryFn: fetchConversionGoals,
  });

  const automations = data?.automations || [];
  const triggers = data?.triggers || [];
  const templates = templatesData?.templates || [];
  const goals = goalsData?.goals || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/automations"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/overview"] });
  };

  const seedMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/comms/automations/seed")).json(),
    onSuccess: (res: { created?: number }) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/templates"] });
      toast({
        title: "Default automations created",
        description: `${formatNumber(res.created)} automations and their starter templates are in place, all switched off.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Could not seed the automations", description: errorMessage(e), variant: "destructive" }),
  });

  const patchMutation = useMutation({
    mutationFn: async (payload: { id: string; body: Record<string, unknown> }) =>
      (await apiRequest("PATCH", `/api/admin/comms/automations/${payload.id}`, payload.body)).json(),
    onSuccess: () => invalidate(),
    onError: (e: unknown) => {
      invalidate();
      toast({ title: "Could not update the automation", description: errorMessage(e), variant: "destructive" });
    },
  });

  const runMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/comms/automations/run")).json(),
    onSuccess: (res: { queued?: number; skipped?: number }) => {
      invalidate();
      toast({ title: "Automations evaluated", description: `${formatNumber(res.queued)} queued, ${formatNumber(res.skipped)} skipped.` });
    },
    onError: (e: unknown) => toast({ title: "Could not run automations", description: errorMessage(e), variant: "destructive" }),
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">How automations work</CardTitle>
              <CardDescription>
                Every enabled automation is evaluated once an hour. Anything it queues still respects quiet hours, the daily sending cap and the
                per-contact frequency cap, and marketing is never sent to a student contact. Turning one on is safe: it only reaches contacts that
                match its trigger from that point on.
              </CardDescription>
            </div>
            {automations.length > 0 && (
              <Button variant="outline" onClick={() => runMutation.mutate()} disabled={runMutation.isPending} data-testid="button-run-automations-tab">
                {runMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Run now
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {error ? (
        <Card><CardContent><ErrorState error={error} /></CardContent></Card>
      ) : isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      ) : automations.length === 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4" /> Set up the default automations</CardTitle>
            <CardDescription>
              This creates the ten standard automations and a starter template for each one. They all arrive switched off, so nothing is sent until
              you turn one on.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <EmptyState
              title="No automations yet"
              hint="Nothing has been set up on this environment."
              action={
                <Button onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending} data-testid="button-seed-automations">
                  {seedMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Set up the default automations
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {automations.map(a => (
            <AutomationCard
              key={a.id}
              automation={a}
              trigger={triggers.find(t => t.id === a.trigger)}
              triggers={triggers}
              templates={templates}
              goals={goals}
              pending={patchMutation.isPending}
              onPatch={(id, body) => patchMutation.mutate({ id, body })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
